// 複雑な変換ロジック用のヘルパー関数

/**
 * ステップ10の業務システムの属性を変換する
 * @param {Array} businessSystems - 業務システム配列
 * @param {Array} existingBusinessSystems - 既存の業務システム
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} 変換された属性オブジェクト
 */
export const transformBusinessSystemsAttributes = (businessSystems, existingBusinessSystems, isEditMode) => {
  const businessSystemsAttributes = {};
  let attributeIndex = 0;

  // 現在の業務システムデータを正規化
  const currentBusinessSystemRecords = normalizeBusinessSystemRecords(businessSystems);

  if (isEditMode && existingBusinessSystems) {
    // 既存の業務システムを処理
    existingBusinessSystems.forEach((existingBusinessSystem) => {
      const currentRecord = currentBusinessSystemRecords.find(current =>
        current.business_system_name === existingBusinessSystem.business_system_name &&
        current.business_system_product_language_name === existingBusinessSystem.business_system_product_language_name
      );

      businessSystemsAttributes[attributeIndex.toString()] = {
        id: existingBusinessSystem.id,
        business_system_id: existingBusinessSystem.business_system_id || '',
        business_system_name: existingBusinessSystem.business_system_name || '',
        business_system_product_language_id: existingBusinessSystem.business_system_product_language_id || '',
        business_system_product_language_name: existingBusinessSystem.business_system_product_language_name || '',
        _destroy: currentRecord ? 'false' : 'true'
      };
      attributeIndex++;
    });

    // 新しく追加された業務システムを処理
    currentBusinessSystemRecords.forEach((currentRecord) => {
      const isNewRecord = !existingBusinessSystems.some(existing =>
        existing.business_system_name === currentRecord.business_system_name &&
        existing.business_system_product_language_name === currentRecord.business_system_product_language_name
      );

      if (isNewRecord) {
        businessSystemsAttributes[attributeIndex.toString()] = {
          business_system_id: currentRecord.business_system_id,
          business_system_name: currentRecord.business_system_name,
          business_system_product_language_id: '',
          business_system_product_language_name: currentRecord.business_system_product_language_name,
          _destroy: 'false'
        };
        attributeIndex++;
      }
    });
  } else {
    // 新規作成の場合
    currentBusinessSystemRecords.forEach((record, index) => {
      businessSystemsAttributes[index.toString()] = {
        business_system_id: record.business_system_id,
        business_system_name: record.business_system_name,
        business_system_product_language_id: '',
        business_system_product_language_name: record.business_system_product_language_name,
        _destroy: 'false'
      };
    });
  }

  return businessSystemsAttributes;
};

/**
 * ステップ10の業務システムレコードを正規化する
 * @param {Array} businessSystems - 業務システム配列
 * @returns {Array} 正規化されたレコード配列
 */
const normalizeBusinessSystemRecords = (businessSystems) => {
  const records = [];
  if (businessSystems && businessSystems.length > 0) {
    businessSystems.forEach((businessSystem) => {
      if ((businessSystem.business_system_name && businessSystem.business_system_name.trim() !== '') ||
          (businessSystem.business_system_product_language_name && businessSystem.business_system_product_language_name.trim() !== '')) {

        if (businessSystem.business_system_product_language_name && businessSystem.business_system_product_language_name.trim() !== '') {
          const productLanguages = businessSystem.business_system_product_language_name.split(',').map(s => s.trim()).filter(s => s !== '');
          productLanguages.forEach((productLanguageName) => {
            records.push({
              business_system_name: businessSystem.business_system_name || '',
              business_system_id: businessSystem.business_system_id || '',
              business_system_product_language_name: productLanguageName
            });
          });
        } else {
          records.push({
            business_system_name: businessSystem.business_system_name || '',
            business_system_id: businessSystem.business_system_id || '',
            business_system_product_language_name: ''
          });
        }
      }
    });
  }
  return records;
};

/**
 * ステップ11のクラウドサービスの属性を変換する
 * @param {Array} cloudServices - クラウドサービス配列
 * @param {Array} existingCloudServices - 既存のクラウドサービス
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} 変換された属性オブジェクト
 */
export const transformCloudServicesAttributes = (cloudServices, existingCloudServices, isEditMode) => {
  const cloudServicesAttributes = {};
  let attributeIndex = 0;

  // 現在のクラウドサービスデータを正規化
  const currentCloudServiceRecords = normalizeCloudServiceRecords(cloudServices);

  if (isEditMode && existingCloudServices) {
    // 既存のクラウドサービスを処理
    existingCloudServices.forEach((existingCloudService) => {
      const currentRecord = currentCloudServiceRecords.find(current =>
        current.cloud_service_name === existingCloudService.cloud_service_name &&
        current.cloud_service_product_name === existingCloudService.cloud_service_product_name
      );

      cloudServicesAttributes[attributeIndex.toString()] = {
        id: existingCloudService.id,
        cloud_service_id: existingCloudService.cloud_service_id || '',
        cloud_service_name: existingCloudService.cloud_service_name || '',
        cloud_service_product_id: existingCloudService.cloud_service_product_id || '',
        cloud_service_product_name: existingCloudService.cloud_service_product_name || '',
        _destroy: currentRecord ? 'false' : 'true'
      };
      attributeIndex++;
    });

    // 新しく追加されたクラウドサービスを処理
    currentCloudServiceRecords.forEach((currentRecord) => {
      const isNewRecord = !existingCloudServices.some(existing =>
        existing.cloud_service_name === currentRecord.cloud_service_name &&
        existing.cloud_service_product_name === currentRecord.cloud_service_product_name
      );

      if (isNewRecord) {
        cloudServicesAttributes[attributeIndex.toString()] = {
          cloud_service_id: currentRecord.cloud_service_id,
          cloud_service_name: currentRecord.cloud_service_name,
          cloud_service_product_id: '',
          cloud_service_product_name: currentRecord.cloud_service_product_name,
          _destroy: 'false'
        };
        attributeIndex++;
      }
    });
  } else {
    // 新規作成の場合
    currentCloudServiceRecords.forEach((record, index) => {
      cloudServicesAttributes[index.toString()] = {
        cloud_service_id: record.cloud_service_id,
        cloud_service_name: record.cloud_service_name,
        cloud_service_product_id: '',
        cloud_service_product_name: record.cloud_service_product_name,
        _destroy: 'false'
      };
    });
  }

  return cloudServicesAttributes;
};

/**
 * ステップ11のクラウドサービスレコードを正規化する
 * @param {Array} cloudServices - クラウドサービス配列
 * @returns {Array} 正規化されたレコード配列
 */
const normalizeCloudServiceRecords = (cloudServices) => {
  const records = [];
  if (cloudServices && cloudServices.length > 0) {
    cloudServices.forEach((cloudService) => {
      if ((cloudService.cloud_service_name && cloudService.cloud_service_name.trim() !== '') ||
          (cloudService.cloud_service_product_name && cloudService.cloud_service_product_name.trim() !== '')) {

        if (cloudService.cloud_service_product_name && cloudService.cloud_service_product_name.trim() !== '') {
          const products = cloudService.cloud_service_product_name.split(',').map(s => s.trim()).filter(s => s !== '');
          products.forEach((productName) => {
            records.push({
              cloud_service_name: cloudService.cloud_service_name || '',
              cloud_service_id: cloudService.cloud_service_id || '',
              cloud_service_product_name: productName
            });
          });
        } else {
          records.push({
            cloud_service_name: cloudService.cloud_service_name || '',
            cloud_service_id: cloudService.cloud_service_id || '',
            cloud_service_product_name: ''
          });
        }
      }
    });
  }
  return records;
};

/**
 * ステップ12のサーバーOSの属性を変換する
 * @param {Array} serverOperatingSystems - サーバーOS配列
 * @param {Array} existingServerOperatingSystems - 既存のサーバーOS
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} 変換された属性オブジェクト
 */
export const transformServerOperatingSystemsAttributes = (serverOperatingSystems, existingServerOperatingSystems, isEditMode) => {
  const serverOperatingSystemsAttributes = {};
  let attributeIndex = 0;

  // 現在のサーバーOSデータを正規化
  const currentServerOperatingSystemRecords = normalizeServerOperatingSystemRecords(serverOperatingSystems);

  if (isEditMode && existingServerOperatingSystems) {
    // 既存のサーバーOSを処理
    existingServerOperatingSystems.forEach((existingServerOS) => {
      const currentRecord = currentServerOperatingSystemRecords.find(current =>
        current.server_operating_system_name === existingServerOS.server_operating_system_name &&
        current.server_environment_name === existingServerOS.server_environment_name
      );

      serverOperatingSystemsAttributes[attributeIndex.toString()] = {
        id: existingServerOS.id,
        server_operating_system_id: existingServerOS.server_operating_system_id || '',
        server_operating_system_name: existingServerOS.server_operating_system_name || '',
        server_environment_id: existingServerOS.server_environment_id || '',
        server_environment_name: existingServerOS.server_environment_name || '',
        _destroy: currentRecord ? 'false' : 'true'
      };
      attributeIndex++;
    });

    // 新しく追加されたサーバーOSを処理
    currentServerOperatingSystemRecords.forEach((currentRecord) => {
      const isNewRecord = !existingServerOperatingSystems.some(existing =>
        existing.server_operating_system_name === currentRecord.server_operating_system_name &&
        existing.server_environment_name === currentRecord.server_environment_name
      );

      if (isNewRecord) {
        serverOperatingSystemsAttributes[attributeIndex.toString()] = {
          server_operating_system_id: currentRecord.server_operating_system_id,
          server_operating_system_name: currentRecord.server_operating_system_name,
          server_environment_id: '',
          server_environment_name: currentRecord.server_environment_name,
          _destroy: 'false'
        };
        attributeIndex++;
      }
    });
  } else {
    // 新規作成の場合
    currentServerOperatingSystemRecords.forEach((record, index) => {
      serverOperatingSystemsAttributes[index.toString()] = {
        server_operating_system_id: record.server_operating_system_id,
        server_operating_system_name: record.server_operating_system_name,
        server_environment_id: '',
        server_environment_name: record.server_environment_name,
        _destroy: 'false'
      };
    });
  }

  return serverOperatingSystemsAttributes;
};

/**
 * ステップ12のサーバーOSレコードを正規化する
 * @param {Array} serverOperatingSystems - サーバーOS配列
 * @returns {Array} 正規化されたレコード配列
 */
const normalizeServerOperatingSystemRecords = (serverOperatingSystems) => {
  const records = [];
  if (serverOperatingSystems && serverOperatingSystems.length > 0) {
    serverOperatingSystems.forEach((serverOS) => {
      if ((serverOS.server_operating_system_name && serverOS.server_operating_system_name.trim() !== '') ||
          (serverOS.server_environment_name && serverOS.server_environment_name.trim() !== '')) {

        if (serverOS.server_environment_name && serverOS.server_environment_name.trim() !== '') {
          const environments = serverOS.server_environment_name.split(',').map(s => s.trim()).filter(s => s !== '');
          environments.forEach((environmentName) => {
            records.push({
              server_operating_system_name: serverOS.server_operating_system_name || '',
              server_operating_system_id: serverOS.server_operating_system_id || '',
              server_environment_name: environmentName
            });
          });
        } else {
          records.push({
            server_operating_system_name: serverOS.server_operating_system_name || '',
            server_operating_system_id: serverOS.server_operating_system_id || '',
            server_environment_name: ''
          });
        }
      }
    });
  }
  return records;
};

/**
 * ステップ13のミドルウェアの属性を変換する
 * @param {Array} middlewares - ミドルウェア配列
 * @param {Array} existingMiddlewares - 既存のミドルウェア
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} 変換された属性オブジェクト
 */
export const transformMiddlewaresAttributes = (middlewares, existingMiddlewares, isEditMode) => {
  const middlewaresAttributes = {};
  let attributeIndex = 0;

  // 現在のミドルウェアデータを正規化
  const currentMiddlewareRecords = normalizeMiddlewareRecords(middlewares);

  if (isEditMode && existingMiddlewares) {
    // 既存のミドルウェアを処理
    existingMiddlewares.forEach((existingMiddleware) => {
      const currentRecord = currentMiddlewareRecords.find(current =>
        current.middleware_name === existingMiddleware.middleware_name &&
        current.middleware_product_name === existingMiddleware.middleware_product_name
      );

      middlewaresAttributes[attributeIndex.toString()] = {
        id: existingMiddleware.id,
        middleware_id: existingMiddleware.middleware_id || '',
        middleware_name: existingMiddleware.middleware_name || '',
        middleware_product_id: existingMiddleware.middleware_product_id || '',
        middleware_product_name: existingMiddleware.middleware_product_name || '',
        _destroy: currentRecord ? 'false' : 'true'
      };
      attributeIndex++;
    });

    // 新しいミドルウェアを追加
    currentMiddlewareRecords.forEach((record) => {
      const alreadyExists = existingMiddlewares.some(existing =>
        existing.middleware_name === record.middleware_name &&
        existing.middleware_product_name === record.middleware_product_name
      );

      if (!alreadyExists) {
        middlewaresAttributes[attributeIndex.toString()] = {
          middleware_id: record.middleware_id || '',
          middleware_name: record.middleware_name || '',
          middleware_product_id: record.middleware_product_id || '',
          middleware_product_name: record.middleware_product_name || ''
        };
        attributeIndex++;
      }
    });
  } else {
    // 新規作成モード
    currentMiddlewareRecords.forEach((record) => {
      middlewaresAttributes[attributeIndex.toString()] = {
        middleware_id: record.middleware_id || '',
        middleware_name: record.middleware_name || '',
        middleware_product_id: record.middleware_product_id || '',
        middleware_product_name: record.middleware_product_name || ''
      };
      attributeIndex++;
    });
  }

  return middlewaresAttributes;
};

/**
 * ステップ13のミドルウェアレコードを正規化する
 * @param {Array} middlewares - ミドルウェア配列
 * @returns {Array} 正規化されたレコード配列
 */
const normalizeMiddlewareRecords = (middlewares) => {
  const records = [];
  if (middlewares && middlewares.length > 0) {
    middlewares.forEach((middleware) => {
      // ミドルウェア名と製品名の両方が入力されている場合のみ処理
      if ((middleware.middleware_name && middleware.middleware_name.trim() !== '') &&
          (middleware.middleware_product_name && middleware.middleware_product_name.trim() !== '')) {

        const products = middleware.middleware_product_name.split(',').map(s => s.trim()).filter(s => s !== '');
        products.forEach((productName) => {
          records.push({
            middleware_name: middleware.middleware_name || '',
            middleware_id: middleware.middleware_id || '',
            middleware_product_name: productName
          });
        });
      }
    });
  }
  return records;
};
